import { useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { useDriverStateV2 } from './useDriverStateV2';
import { resetAllQueries } from '@/api/client';
import pushNotificationService from '@/services/pushNotifications';
import oneSignalService from '@/services/oneSignal';
import {
  getStoredSelectedCompanyId,
  getUserCompanies,
  resolveSelectedCompanyId,
  SELECTED_COMPANY_KEY,
} from './userCompanies.shared';
import type { UserCompaniesResponseDto } from '@relay-towing/api';

export interface UseUserCompaniesResult {
  companies: UserCompaniesResponseDto['companies'];
  selectedCompanyId: string | null;
  selectCompany: (companyId: string) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook that exposes the full list of companies the current user belongs to,
 * and manages the selected active company in localStorage.
 *
 * Selected company persists across sessions via `relay:selectedCompanyId`.
 * When changing companies, all React Query cache is reset to ensure no stale
 * data, and push notifications are registered again under the new company.
 */
export const useUserCompanies = (): UseUserCompaniesResult => {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-companies', driver?.userId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      if (!driver?.userId) {
        throw new Error('User ID not available');
      }
      return getUserCompanies(token, driver.userId);
    },
    enabled: !!driver?.userId,
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  const companies = data?.companies ?? [];
  const storedId = getStoredSelectedCompanyId();
  const selectedCompanyId = resolveSelectedCompanyId(companies, storedId);

  useEffect(() => {
    if (selectedCompanyId && selectedCompanyId !== storedId) {
      localStorage.setItem(SELECTED_COMPANY_KEY, selectedCompanyId);
    }
  }, [selectedCompanyId, storedId]);

  const selectCompany = useCallback(async (companyId: string) => {
    localStorage.setItem(SELECTED_COMPANY_KEY, companyId);
    await resetAllQueries();
    // This device's push registration names a company too; move it, or the
    // driver hears about the old company's calls and not the new one's.
    await Promise.allSettled([
      pushNotificationService.registerForSelectedCompany(),
      oneSignalService.registerForSelectedCompany(),
    ]);
  }, []);

  return {
    companies,
    selectedCompanyId,
    selectCompany,
    isLoading,
    error: error as Error | null,
  };
};
